import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { CreditCard, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { PaymentKPIs, ChartTheme } from './types';
import { KPICard, KPICardGrid } from './KPICard';
import { getBaseChartOptions, formatDuration } from './chartUtils';
import { useTranslation } from 'react-i18next';

interface PaymentSectionProps {
    data: PaymentKPIs;
    theme: ChartTheme;
}

export const PaymentSection: React.FC<PaymentSectionProps> = ({ data, theme }) => {
    const { t } = useTranslation();
    const baseOptions = getBaseChartOptions(theme);

    const paymentStatusLabels: Record<string, string> = {
        paid: t('customerOrders.paymentStatus.paid'),
        pending: t('customerOrders.paymentStatus.pending'),
        failed: t('customerOrders.paymentStatus.failed'),
        canceled: t('customerOrders.paymentStatus.canceled'),
        disputed: t('customerOrders.paymentStatus.disputed'),
        refunded_partial: t('customerOrders.paymentStatus.refunded_partial'),
        refunded_full: t('customerOrders.paymentStatus.refunded_full'),
    };

    // Donut Chart - Répartition des statuts de paiement
    const statusChartOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'pie',
            height: 300,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.payment.statusDistribution'),
        },
        tooltip: {
            ...baseOptions.tooltip,
            pointFormat: `<b>{point.percentage:.1f}%</b> ({point.y} ${t('admin.dashboard.analytics.payment.payments').toLowerCase()})`,
        },
        plotOptions: {
            pie: {
                innerSize: '60%',
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b>: {point.percentage:.1f}%',
                    style: { color: theme.textColor, fontSize: '11px' },
                },
                showInLegend: true,
            },
        },
        series: [{
            name: t('admin.dashboard.analytics.payment.status'),
            type: 'pie',
            data: data.statusDistribution.map(s => ({
                name: paymentStatusLabels[s.status] || s.status,
                y: s.count,
                color: s.status === 'paid' ? theme.successColor
                    : s.status === 'pending' ? theme.warningColor
                        : s.status === 'failed' ? theme.errorColor
                            : undefined,
            })),
        }],
    };

    // Bar Chart - Délais de paiement
    const delayChartOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'bar',
            height: 300,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.payment.delayDistribution'),
        },
        xAxis: {
            ...baseOptions.xAxis,
            categories: data.paymentDelays.map(d => d.range),
        },
        yAxis: {
            ...baseOptions.yAxis,
            title: { text: t('admin.dashboard.analytics.payment.paymentsCount'), style: { color: theme.textColor } },
            allowDecimals: false,
        },
        tooltip: {
            ...baseOptions.tooltip,
            formatter: function () {
                return `<b>${this.x}</b><br/>${t('admin.dashboard.analytics.payment.payments')}: ${this.y}`;
            },
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                borderWidth: 0,
                colorByPoint: true,
                colors: [theme.successColor, theme.primaryColor, theme.warningColor, theme.errorColor],
            },
        },
        legend: { enabled: false },
        series: [{
            name: t('admin.dashboard.analytics.payment.payments'),
            type: 'bar',
            data: data.paymentDelays.map(d => d.count),
        }],
    };

    return (
        <section className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-success-10 rounded-lg">
                    <CreditCard className="h-5 w-5 text-success" />
                </div>
                <h2 className="text-xl font-bold text-foreground">{t('admin.dashboard.analytics.payment.title')}</h2>
            </div>

            {/* KPI Cards */}
            <KPICardGrid columns={4}>
                <KPICard
                    title={t('admin.dashboard.analytics.payment.kpis.successRate')}
                    value={data.successRate}
                    format="percentage"
                    icon={<CheckCircle className="h-5 w-5 text-success" />}
                    color="success"
                    tooltip={t('admin.dashboard.analytics.payment.kpis.successRateTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.payment.kpis.paymentDelay')}
                    value={formatDuration(data.medianPaymentDelay)}
                    icon={<Clock className="h-5 w-5 text-primary" />}
                    color="primary"
                    tooltip={t('admin.dashboard.analytics.payment.kpis.paymentDelayTooltip', { count: data.totalPaidOrders })}
                    subtitle={data.totalPaidOrders < 5 ? (
                        <span className="text-[10px] text-warning flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {t('admin.dashboard.analytics.payment.kpis.lowVolume')}
                        </span>
                    ) : (
                        <span className="text-[10px] text-foreground-secondary">
                            {t('admin.dashboard.analytics.payment.kpis.basedOn', { count: data.totalPaidOrders })}
                        </span>
                    )}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.payment.kpis.latePayments')}
                    value={data.latePaymentsPercent}
                    format="percentage"
                    icon={<AlertCircle className={`h-5 w-5 ${data.latePaymentsPercent > 10 ? 'text-warning' : 'text-success'}`} />}
                    color={data.latePaymentsPercent > 10 ? 'warning' : 'success'}
                    tooltip={t('admin.dashboard.analytics.payment.kpis.latePaymentsTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.payment.kpis.pending')}
                    value={data.pendingPayments}
                    format="number"
                    icon={<AlertCircle className="h-5 w-5 text-warning" />}
                    color={data.pendingPayments > 0 ? 'warning' : 'success'}
                    tooltip={t('admin.dashboard.analytics.payment.kpis.pendingTooltip')}
                />
            </KPICardGrid>

            {/* Répartition des méthodes de paiement */}
            {data.methodDistribution.length > 0 && (
                <div className="flex flex-wrap gap-3">
                    {data.methodDistribution.map(method => (
                        <div
                            key={method.method}
                            className="flex items-center gap-2 px-4 py-2 bg-surface border border-border rounded-lg"
                        >
                            <span className="text-lg">
                                {method.method === 'Stripe' ? '💳' : '🅿️'}
                            </span>
                            <span className="font-medium text-foreground">{method.method}</span>
                            <span className="text-foreground-secondary">
                                {method.count} ({method.percentage.toFixed(1)}%)
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-surface border border-border rounded-xl p-4">
                    <HighchartsReact highcharts={Highcharts} options={statusChartOptions} />
                </div>
                <div className="bg-surface border border-border rounded-xl p-4">
                    <HighchartsReact highcharts={Highcharts} options={delayChartOptions} />
                </div>
            </div>
        </section>
    );
};
